/**
 * User-facing action error presentation.
 * Maps common app actions + error details to calm, specific toast messages.
 * Replaces generic "Failed to X" patterns across App.tsx catch blocks.
 */
import axios from 'axios';
import { isTransientNetworkError } from './networkRetry';

export interface ActionErrorResult {
  title: string;
  description: string;
}

export interface ActionErrorContext {
  /** Recipient name for signing actions */
  recipientName?: string;
  /** File or document name */
  fileName?: string;
  /** Export format (pdf, docx, etc.) */
  format?: string;
}

/**
 * Classify an error and return user-facing toast-ready messaging.
 *
 * @param action - What the user was trying to do (e.g. "send signing request")
 * @param err - The caught error (any type)
 * @param context - Optional additional context for richer messaging
 */
export function getActionError(
  action: string,
  err: unknown,
  context?: ActionErrorContext,
): ActionErrorResult {
  const status = axios.isAxiosError(err) ? err.response?.status : undefined;
  const serverMsg = axios.isAxiosError(err)
    ? (err.response?.data as Record<string, unknown> | undefined)?.message as string | undefined
    : undefined;
  const serverCode = axios.isAxiosError(err)
    ? (err.response?.data as Record<string, unknown> | undefined)?.code as string | undefined
    : undefined;

  // Auth expired
  if (status === 401) {
    return { title: 'Sign-in required', description: 'Your session expired. Please sign in and try again.' };
  }

  // Rate limited — use backend message if available (e.g., reminder cooldown)
  if (status === 429) {
    return { title: 'Too many requests', description: serverMsg ?? 'Please wait a moment before trying again.' };
  }

  // Plan limit
  if (status === 402) {
    return { title: 'Plan limit reached', description: serverMsg ?? `You need to upgrade your plan to ${action}.` };
  }

  // Forbidden
  if (status === 403) {
    if (serverCode === 'EMAIL_VERIFICATION_REQUIRED') {
      return { title: 'Verification required', description: 'Please verify your email address first.' };
    }
    return { title: 'Access denied', description: serverMsg ?? `You don't have permission to ${action}.` };
  }

  // Validation / bad input
  if (status === 400 || status === 422) {
    return { title: 'Invalid request', description: serverMsg ?? `Something was wrong with your request to ${action}. Please check and try again.` };
  }

  // Not found
  if (status === 404) {
    return { title: 'Not found', description: serverMsg ?? `The resource needed to ${action} could not be found.` };
  }

  // Server / gateway errors
  if (status && status >= 500) {
    const file = context?.fileName ? ` for "${context.fileName}"` : '';
    return { title: `Couldn't ${action}`, description: `Something went wrong on our end${file}. Please try again in a moment.` };
  }

  // Network error (no status)
  if (axios.isAxiosError(err) && !status) {
    return { title: `Couldn't ${action}`, description: 'Check your internet connection and try again.' };
  }

  // fetch / streams / Wi‑Fi–VPN handoff (no axios wrapper)
  if (!axios.isAxiosError(err) && isTransientNetworkError(err)) {
    return {
      title: `Couldn't ${action}`,
      description:
        'Your connection dropped or changed (for example Wi‑Fi or VPN). Wait a second and try again — the app retries automatically when it can.',
    };
  }

  // Fallback: use error message if available, with file context
  const raw = err instanceof Error ? err.message : undefined;
  const file = context?.fileName ? ` for "${context.fileName}"` : '';
  return {
    title: `Couldn't ${action}`,
    description: raw ?? `Something went wrong${file}. Please try again.`,
  };
}
